/**
 * DSD 표의 행·열마다 그 줄의 값이 IXD 의 어느 항목·기간·멤버로 들어갔는지 모은다 —
 * 머리 라벨용.
 *
 * 판정하지 않는다. 행 전체가 엉뚱한 IXD 항목으로 가면 줄 안의 칸끼리는 서로 일치해
 * 다수결로는 드러나지 않는다. 그래서 목적지가 하나여도 라벨을 내고, 머리 이름과의
 * 대조는 사람이 한다.
 *
 * WHY: IXD 미리보기의 행·열 위치가 아니라 정본 cellId 의 fact 좌표(항목·기간·멤버·누적
 * 구분)를 쓴다. DSD 행이 IXD 열에 대응하거나(전치), DSD 행·열이 IXD 행 하나로 접히는
 * 등 화면 축의 대응은 표마다 다르다. 그래서 표마다 "DSD 행 안에서는 같고 행끼리는
 * 다른 좌표" 를 다수결로 골라 그 좌표로 행을 부른다. 다수결이라 일부 칸이 엉뚱한 곳에
 * 들어가도 축 분류는 흔들리지 않는다.
 */

import type { DsdParsedCell } from "@/apis/compare/types";
import { DsdCellText } from "@/utils/dsd-cell-text";
import { expandSpanGrid, spanGridKey } from "@/utils/expand-span-grid";
import { parseCellId } from "@/utils/ixd-cell-model";

/** fact 좌표 id → 화면 문구. 항목·멤버는 IXD 머리 텍스트, 기간은 DL 의 기간 라벨. */
export interface FactLabels {
  lineItem: (lineItemId: string) => string;
  member: (memberId: string) => string;
  period: (periodType: string, dlViewId: string | null) => string;
}

export type CounterpartAxis = "row" | "col";

export interface CounterpartDestination {
  label: string;
  dsdIds: string[];
}

/** 줄 안의 칸 하나가 IXD 어디에 들어갔는지 — 검토 카드의 칸별 대응표 한 줄 */
export interface CounterpartCell {
  dsdId: string;
  /**
   * 이 칸의 다른 축 머리 칸(행 줄이면 열 머리, 열 줄이면 행 머리). 그 축 대응이 없으면 null.
   */
  crossAnchorDsdId: string | null;
  /** 이 줄을 가르는 좌표의 IXD 이름 — `destinations` 의 라벨과 같다. 대응이 없으면 null */
  axisLabel: string | null;
  /** 나머지 좌표(기간·멤버 등)의 IXD 이름. 비어 있을 수 있다. 대응이 없으면 null */
  restLabel: string | null;
}

export interface HeaderCounterpart {
  axis: CounterpartAxis;
  /**
   * 이 줄을 이루는 DL 영역들(서버 추론, 첫 등장 순). 한 DSD 표가 DL 여럿에 걸치면 행·열
   * 구조가 같은 영역끼리 묶어 줄을 만들고, 구조가 다른 영역은 따로 줄을 만든다. 영역이
   * 배정되지 않은 칸은 null 로 들어간다.
   */
  dlViewIds: (string | null)[];
  /** 펼친 격자 기준 행 또는 열 번호 */
  index: number;
  /** 라벨을 붙일 칸. 줄의 머리 칸이고, 못 찾으면 그 줄의 첫 값 칸이다. */
  anchorDsdId: string;
  /** 첫 등장 순. 둘 이상이면 값이 IXD 여러 곳으로 갈라진 것이다. */
  destinations: CounterpartDestination[];
  /** 태깅이 필요한데 대응 IXD 셀이 없는 칸 (should-tag) */
  unmatchedDsdIds: string[];
  /**
   * 목적지가 하나이고 그 라벨이 머리 칸 글자와 같은가 — 같으면 라벨을 되풀이하지 않고
   * "같음" 표시만 한다. 머리 칸을 못 찾아 값 칸에 붙은 경우는 비교할 이름이 없어 false.
   */
  matchesHeader: boolean;
  /** 줄 안의 칸들 — 위치 순서(행 줄은 왼쪽부터, 열 줄은 위부터) */
  cells: CounterpartCell[];
}

const FACT_KINDS = ["lineItem", "period", "member", "accumulated"] as const;
type FactKind = (typeof FACT_KINDS)[number];
type Fact = Record<FactKind, string>;

interface Member {
  cell: DsdParsedCell;
  row: number;
  col: number;
  /** 정본 셀이 없으면 null (should-tag) */
  fact: Fact | null;
  /** 정본 셀이 들어간 IXD DL — 기간 라벨에 쓴다 */
  dlViewId: string | null;
  /** 서버가 이 DSD 칸에 배정한 DL 영역. 없으면 null */
  region: string | null;
}

// 값 자리(숫자·「-」·빈 칸)는 머리로 쓰지 않는다. 매칭된 글자 칸(「개량신약」 등)은 머리가
// 될 수 있다 — 대응 집계는 숫자 칸만 하기 때문이다.
const isHeaderCandidate = (
  cell: DsdParsedCell | null | undefined,
): cell is DsdParsedCell => !!cell && !DsdCellText.of(cell.text).isValueLike;

// 머리 앞 순번(「9. 」「Ⅰ. 」「IV. 」「(1) 」「가. 」), 공백·줄바꿈(「구  분」), 택소노미
// 라벨의 「[구성요소]」 꼬리는 이름 차이로 보지 않는다. 「(손실)」 같은 말 차이는 그대로 둔다.
//
// WHY: 한글 순번은 글자를 열거한다. `[가-하]` 로 쓰면 음절 1만여 자가 들어와 「(주)한국전자」
// 가 「한국전자」 와 같은 이름이 돼 버린다 — 회사명 머리가 많은 표에서 확인할 줄이 통째로
// 빠진다. 놓치는 쪽이 더 비싸므로 범위를 쓰지 않는다.
const LEADING_ORDINAL =
  /^\s*(?:\(?\d+\)|\d+\.|[IVXⅠ-Ⅻ]+\.|\(?[가나다라마바사아자차카타파하]\)|[가나다라마바사아자차카타파하]\.)\s*/;

const NO_MEMBER_LABEL = "멤버 없음";

/** 머리 칸 글자와 IXD 이름이 같은 이름인가 — `matchesHeader` 와 같은 기준(순번·공백·[구성요소] 무시) */
export const isSameHeaderName = (headerText: string, label: string): boolean =>
  isSameName(headerText, label);

// 멤버 없는 칸(축의 합계)은 IXD 쪽에 비교할 이름이 없다. 머리가 합계 말이면 같은 자리로 본다.
// 합계 말이 아닌 머리가 멤버 없는 칸으로 갔으면 그대로 "다름" 이다 — 열이 합계로 밀린 경우다.
const TOTAL_HEADER_NAMES = new Set(["합계", "계", "총계", "소계", "총합계"]);

const isSameName = (headerText: string, label: string): boolean => {
  const header = normalizeName(headerText);
  if (label === NO_MEMBER_LABEL) return TOTAL_HEADER_NAMES.has(header);
  return header === normalizeName(label);
};

const normalizeName = (text: string): string =>
  text
    .replace(LEADING_ORDINAL, "")
    .replace(/\[구성요소\]/g, "")
    .replace(/\s+/g, "");

const ACCUMULATED_LABELS: Record<string, string> = {
  ACC: "누적",
  QUARTER_ACC: "3개월",
};

const factOf = (cellId: string): Fact => {
  const { lineItemId, axisIds, periodType, accumulatedType } =
    parseCellId(cellId);
  return {
    lineItem: lineItemId,
    member: [...axisIds].sort().join("|"),
    period: periodType,
    accumulated: accumulatedType,
  };
};

const countValues = (facts: Fact[], kind: FactKind): Map<string, number> => {
  const counts = new Map<string, number>();
  for (const fact of facts)
    counts.set(fact[kind], (counts.get(fact[kind]) ?? 0) + 1);
  return counts;
};

/** 줄 안에서 그 좌표가 과반 칸에서 같은 값인가. 2칸이 서로 다르면 "다름" 이다. */
const isMostlySame = (facts: Fact[], kind: FactKind): boolean =>
  Math.max(...countValues(facts, kind).values()) * 2 > facts.length;

const dominantValue = (facts: Fact[], kind: FactKind): string =>
  [...countValues(facts, kind).entries()].sort((a, b) => b[1] - a[1])[0][0];

/**
 * 그 좌표가 이 방향의 줄들을 가르는가 — 줄 안에서는 대체로 같고, 줄마다 대표값이 다르다.
 *
 * "줄 안에서 같음" 은 2칸 이상인 줄의 칸 수로 가중 투표한다(근거가 없으면 통과). "줄마다
 * 다름" 은 줄별 대표값 가운데 가장 흔한 값이 줄의 절반 이하일 때다 — 일부 칸이 엉뚱한
 * 곳에 들어가도 대표값은 그대로라 분류가 흔들리지 않는다.
 */
const separatesLines = (lines: Fact[][], kind: FactKind): boolean => {
  let same = 0;
  let diff = 0;
  for (const facts of lines) {
    if (facts.length < 2) continue;
    if (isMostlySame(facts, kind)) same += facts.length;
    else diff += facts.length;
  }
  const dominants = lines
    .filter((facts) => facts.length > 0)
    .map((facts) => dominantValue(facts, kind));
  const dominantCounts = new Map<string, number>();
  for (const value of dominants)
    dominantCounts.set(value, (dominantCounts.get(value) ?? 0) + 1);
  const topShare = Math.max(0, ...dominantCounts.values());
  return (
    same >= diff && dominants.length > 1 && topShare * 2 <= dominants.length
  );
};

/** 표의 행·열이 각각 어떤 fact 좌표로 갈리는지 정한다. 표 전체에서 같은 좌표는 어느 쪽에도 없다. */
const classifyKinds = (
  rows: Fact[][],
  cols: Fact[][],
): Record<CounterpartAxis, FactKind[]> => {
  const row = FACT_KINDS.filter((kind) => separatesLines(rows, kind));
  const col = FACT_KINDS.filter(
    (kind) => !row.includes(kind) && separatesLines(cols, kind),
  );
  return { row, col };
};

const describeFact = (
  fact: Fact,
  dlViewId: string | null,
  kinds: readonly FactKind[],
  labels: FactLabels,
): string =>
  kinds
    .map((kind) => {
      const value = fact[kind];
      if (kind === "lineItem") return labels.lineItem(value);
      if (kind === "member")
        return value
          ? value.split("|").map(labels.member).join(" · ")
          : NO_MEMBER_LABEL;
      if (kind === "period") return labels.period(value, dlViewId);
      return ACCUMULATED_LABELS[value] ?? value;
    })
    .map((text) => text.trim())
    .filter(Boolean)
    .join(" · ");

/**
 * 표의 행·열별 IXD 대응을 만든다. 대응할 값 칸이 없는 행·열(머리 행, 라벨만 있는 행)은
 * 결과에 없다 — 그 빈자리는 아무것도 주장하지 않는다.
 *
 * 축을 가를 근거가 없는 줄(1×1 표, 값 열이 하나뿐인 표의 열 등)은 그 줄 안에서 같은
 * 좌표를 모두 라벨로 낸다. 라벨을 숨기면 "확인할 게 없는 줄" 로 읽힌다.
 *
 * @param cells - 서버가 준 HTML 순서의 표 셀. span 은 여기서 펼친다.
 * @param labels - fact 좌표 id → 화면 문구.
 * @param headerRowIndices - 서버가 열 헤더로 판정한 행 번호. 있으면 열 라벨은 그 행
 *   안에서만 머리 칸을 찾는다(없으면 그 열의 첫 값 칸). null·생략은 판정이 없는 구버전
 *   산출물이라 값 칸 위로 올라가며 처음 만나는 글자 칸을 머리로 추정한다.
 * @returns 행 대응이 먼저, 열 대응이 뒤에 오고 각각 번호 순이다.
 */
export const buildTableCounterparts = (
  cells: readonly (readonly (DsdParsedCell | null)[])[],
  labels: FactLabels,
  headerRowIndices?: readonly number[] | null,
  regionOf: (dsdId: string) => string | null = () => null,
): HeaderCounterpart[] => {
  const { grid, originIndex } = expandSpanGrid(cells, (cell) => cell ?? {});

  const members: Member[] = [];
  cells.forEach((row, rowIndex) =>
    row.forEach((cell, colIndex) => {
      // WHY: 글자 값(「3상」 등)은 표 안에서 되풀이돼 값 매칭이 위치를 하나로 못 정한다.
      // 그 대응을 섞으면 라벨이 갈라짐으로 시끄러워지므로 숫자 칸만 센다.
      if (!cell || !DsdCellText.of(cell.text).isNumericValue) return;
      const origin = originIndex.get(spanGridKey(rowIndex, colIndex));
      if (!origin) return;
      const proper = cell.mostProperCell;
      if (!proper && cell.matchStatus !== "should-tag") return;
      members.push({
        cell,
        row: origin.row,
        col: origin.col,
        fact: proper ? factOf(proper.cellId) : null,
        dlViewId: proper?.dlViewId ?? null,
        region: regionOf(cell.dsdId),
      });
    }),
  );

  // WHY: 위로 올라가며 추정하면 「유동자산」 같은 본문 구분 행에 걸린다. 서버 판정은
  // 그런 행을 헤더에서 빼므로, 있으면 헤더 행 안에서 아래부터 고른다.
  const headerRowsBottomUp = headerRowIndices
    ? [...headerRowIndices].sort((a, b) => b - a)
    : null;

  const anchorOf = (axis: CounterpartAxis, first: Member): DsdParsedCell => {
    if (axis === "col" && headerRowsBottomUp) {
      for (const row of headerRowsBottomUp) {
        const candidate = grid[row]?.[first.col]?.item;
        if (isHeaderCandidate(candidate)) return candidate;
      }
      return first.cell;
    }
    const steps = axis === "row" ? first.col : first.row;
    for (let offset = 1; offset <= steps; offset += 1) {
      const slot =
        axis === "row"
          ? grid[first.row]?.[first.col - offset]
          : grid[first.row - offset]?.[first.col];
      const candidate = slot?.item;
      if (isHeaderCandidate(candidate)) return candidate;
    }
    return first.cell;
  };

  const groupBy = (source: Member[], axis: CounterpartAxis) => {
    const byIndex = new Map<number, Member[]>();
    for (const member of source) {
      const index = axis === "row" ? member.row : member.col;
      const group = byIndex.get(index);
      if (group) group.push(member);
      else byIndex.set(index, [member]);
    }
    return [...byIndex.entries()]
      .sort(([a], [b]) => a - b)
      .map(([index, group]) => ({
        index,
        group: [...group].sort((a, b) =>
          axis === "row" ? a.col - b.col : a.row - b.row,
        ),
      }));
  };
  const factsOf = (group: Member[]) =>
    group.flatMap((member) => (member.fact ? [member.fact] : []));
  const classifyMembers = (source: Member[]) =>
    classifyKinds(
      groupBy(source, "row").map(({ group }) => factsOf(group)),
      groupBy(source, "col").map(({ group }) => factsOf(group)),
    );
  const tableKinds = classifyMembers(members);

  // WHY: 한 표가 DL 여럿에 걸치면 영역마다 IXD 의 행·열 구조가 다를 수 있다(한 영역은 행이
  // 항목, 다른 영역은 열이 항목). 표 전체로 투표하면 섞이므로 서버가 배정한 영역마다 따로
  // 투표하고, 구조가 같은 영역끼리는 다시 묶는다 — 묶지 않으면 영역 경계를 넘는 행이 같은
  // 라벨로 두 번 검토 대상이 된다. 머리 칸은 표 전체 격자에서 찾는다 — 격자까지 자르면 영역
  // 밖 머리 행이 사라진다.
  const membersByRegion = new Map<string | null, Member[]>();
  for (const member of members) {
    const regionMembers = membersByRegion.get(member.region);
    if (regionMembers) regionMembers.push(member);
    else membersByRegion.set(member.region, [member]);
  }
  // 영역 조각이 작아(한 줄뿐 등) 한 축을 가를 근거가 없으면 표 전체 투표를 빌린다. 그래도
  // 없을 때만 줄 안에서 같은 좌표를 모두 라벨로 낸다 — 기간 같은 상수가 이름에 붙는 잡음.
  const kindsOfRegion = (
    regionMembers: Member[],
  ): Record<CounterpartAxis, FactKind[]> => {
    const regionKinds = classifyMembers(regionMembers);
    return {
      row: regionKinds.row.length
        ? regionKinds.row
        : tableKinds.row.filter((kind) => !regionKinds.col.includes(kind)),
      col: regionKinds.col.length
        ? regionKinds.col
        : tableKinds.col.filter((kind) => !regionKinds.row.includes(kind)),
    };
  };
  const structures = new Map<
    string,
    {
      regions: (string | null)[];
      members: Member[];
      kindsByAxis: Record<CounterpartAxis, FactKind[]>;
    }
  >();
  for (const [region, regionMembers] of membersByRegion) {
    const kindsByAxis = kindsOfRegion(regionMembers);
    const key = `${kindsByAxis.row.join(",")}|${kindsByAxis.col.join(",")}`;
    const existing = structures.get(key);
    if (existing) {
      existing.regions.push(region);
      existing.members.push(...regionMembers);
    } else
      structures.set(key, {
        regions: [region],
        members: [...regionMembers],
        kindsByAxis,
      });
  }

  const buildStructure = ({
    regions,
    members: structureMembers,
    kindsByAxis,
  }: {
    regions: (string | null)[];
    members: Member[];
    kindsByAxis: Record<CounterpartAxis, FactKind[]>;
  }): HeaderCounterpart[] => {
    const rowGroups = groupBy(structureMembers, "row");
    const colGroups = groupBy(structureMembers, "col");

    const build = (
      axis: CounterpartAxis,
      groups: { index: number; group: Member[] }[],
    ): (HeaderCounterpart & { group: Member[]; kinds: FactKind[] })[] =>
      groups.map(({ index, group }) => {
        const facts = factsOf(group);
        const otherAxisKinds = kindsByAxis[axis === "row" ? "col" : "row"];
        const kinds = kindsByAxis[axis].length
          ? kindsByAxis[axis]
          : FACT_KINDS.filter(
              (kind) =>
                !otherAxisKinds.includes(kind) &&
                facts.some((fact) => fact[kind]) &&
                isMostlySame(facts, kind),
            );
        const destinations: CounterpartDestination[] = [];
        for (const { cell, fact, dlViewId } of group) {
          if (!fact) continue;
          const label = describeFact(fact, dlViewId, kinds, labels);
          const existing = destinations.find((d) => d.label === label);
          if (existing) existing.dsdIds.push(cell.dsdId);
          else destinations.push({ label, dsdIds: [cell.dsdId] });
        }
        const anchor = anchorOf(axis, group[0]);
        return {
          group,
          kinds: [...kinds],
          cells: [],
          axis,
          dlViewIds: regions,
          index,
          anchorDsdId: anchor.dsdId,
          destinations,
          unmatchedDsdIds: group
            .filter((member) => !member.fact)
            .map((member) => member.cell.dsdId),
          matchesHeader:
            destinations.length === 1 &&
            isHeaderCandidate(anchor) &&
            isSameName(anchor.text, destinations[0].label),
        };
      });

    const rows = build("row", rowGroups);
    const cols = build("col", colGroups);
    const anchorByIndex = (lines: typeof rows) =>
      new Map(lines.map((line) => [line.index, line.anchorDsdId]));
    const rowAnchors = anchorByIndex(rows);
    const colAnchors = anchorByIndex(cols);
    return [...rows, ...cols].map(({ group, kinds, ...counterpart }) => ({
      ...counterpart,
      cells: group.map(({ cell, fact, dlViewId, row, col }) => ({
        dsdId: cell.dsdId,
        crossAnchorDsdId:
          (counterpart.axis === "row"
            ? colAnchors.get(col)
            : rowAnchors.get(row)) ?? null,
        axisLabel: fact ? describeFact(fact, dlViewId, kinds, labels) : null,
        restLabel: fact
          ? describeFact(
              fact,
              dlViewId,
              FACT_KINDS.filter((kind) => !kinds.includes(kind) && fact[kind]),
              labels,
            )
          : null,
      })),
    }));
  };

  return [...structures.values()].flatMap(buildStructure);
};

/** 라벨을 붙일 칸 → 그 칸에 붙는 대응들. 행 머리와 열 머리가 한 칸에 겹칠 수 있다. */
export const groupByAnchor = (
  counterparts: readonly HeaderCounterpart[],
): Map<string, HeaderCounterpart[]> => {
  const map = new Map<string, HeaderCounterpart[]>();
  for (const counterpart of counterparts) {
    const group = map.get(counterpart.anchorDsdId);
    if (group) group.push(counterpart);
    else map.set(counterpart.anchorDsdId, [counterpart]);
  }
  return map;
};

/**
 * 사람이 확인할 줄인가 — 「diff」 는 목적지가 하나인데 이름이 머리와 다르고, 「split」 은
 * 값이 IXD 여러 곳으로 갈라졌다. 이름이 같거나 대응이 하나도 없는 줄(칸 색이 이미
 * 빨강)은 null 이다.
 */
export const counterpartFlag = (
  counterpart: HeaderCounterpart,
): "diff" | "split" | null => {
  if (counterpart.destinations.length > 1) return "split";
  if (counterpart.destinations.length === 1 && !counterpart.matchesHeader)
    return "diff";
  return null;
};
